import { Controller } from "@hotwired/stimulus";
import { DirectUpload } from "@rails/activestorage";

// Connects to data-controller="direct-uploads"
//
// Uploads each selected file immediately via ActiveStorage Direct Upload
// (no form submit), shows it in a table row with a progress bar, and adds a
// hidden `document[files][]` input carrying the returned signed blob id so the
// blob is attached when the form is finally submitted. Lets the reviewer add
// files one at a time without losing previous selections.
export default class extends Controller {
  static targets = ["input", "rows", "pendingContainer"];
  static values = {
    uploadingText: String,
    failedText: String,
    removeText: String,
    sizeErrorText: String,
    maxSize: Number,
  };

  add(event) {
    const url = this.inputTarget.dataset.directUploadUrl;

    Array.from(event.target.files).forEach((file) => {
      if (file.size > this.maxSizeValue) {
        this.buildRow(file, { error: this.sizeErrorTextValue });
        return;
      }
      this.uploadFile(file, url);
    });

    // Reset so the same file can be picked again, and so the raw file is never
    // submitted alongside the signed-id hidden inputs (which would double up).
    this.inputTarget.value = "";
    this.updatePendingVisibility();
  }

  uploadFile(file, url) {
    const row = this.buildRow(file);
    const upload = new DirectUpload(file, url, new ProgressTracker(row));

    upload.create((error, blob) => {
      if (error) {
        this.markFailed(row);
      } else {
        this.markComplete(row, blob);
      }
    });
  }

  remove(event) {
    const row = event.currentTarget.closest("tr");
    if (row) row.remove();
    this.updatePendingVisibility();
  }

  // Builds a pending-upload row. When `error` is given the row is rendered in a
  // terminal failed state (no upload in flight).
  buildRow(file, { error } = {}) {
    const tr = document.createElement("tr");

    const nameCell = document.createElement("td");
    const name = document.createElement("span");
    name.textContent = file.name;
    nameCell.appendChild(name);

    const status = document.createElement("span");
    status.classList.add("backyard-documents__upload-status");
    if (error) {
      status.textContent = error;
    } else {
      status.textContent = this.uploadingTextValue;
      const progress = document.createElement("progress");
      progress.max = 100;
      progress.value = 0;
      nameCell.appendChild(progress);
    }
    nameCell.appendChild(status);
    tr.appendChild(nameCell);

    const actionCell = document.createElement("td");
    actionCell.classList.add("backyard-documents__file-action");
    const removeButton = document.createElement("button");
    removeButton.type = "button";
    removeButton.classList.add("backyard-documents__remove-file");
    removeButton.textContent = this.removeTextValue;
    removeButton.dataset.action = "direct-uploads#remove";
    // Disabled while the upload is in flight; enabled on success/failure.
    removeButton.disabled = !error;
    actionCell.appendChild(removeButton);
    tr.appendChild(actionCell);

    this.rowsTarget.appendChild(tr);
    this.updatePendingVisibility();
    return tr;
  }

  markComplete(row, blob) {
    const progress = row.querySelector("progress");
    if (progress) progress.remove();
    const status = row.querySelector(".backyard-documents__upload-status");
    if (status) status.remove();

    const hidden = document.createElement("input");
    hidden.type = "hidden";
    hidden.name = "document[files][]";
    hidden.value = blob.signed_id;
    row.querySelector("td").appendChild(hidden);

    const removeButton = row.querySelector(".backyard-documents__remove-file");
    if (removeButton) removeButton.disabled = false;
  }

  markFailed(row) {
    const progress = row.querySelector("progress");
    if (progress) progress.remove();
    const status = row.querySelector(".backyard-documents__upload-status");
    if (status) status.textContent = this.failedTextValue;

    const removeButton = row.querySelector(".backyard-documents__remove-file");
    if (removeButton) removeButton.disabled = false;
  }

  updatePendingVisibility() {
    if (!this.hasPendingContainerTarget) return;
    this.pendingContainerTarget.hidden = this.rowsTarget.children.length === 0;
  }
}

// Per-upload delegate: wires the XHR progress event to its row's progress bar.
class ProgressTracker {
  constructor(row) {
    this.row = row;
  }

  directUploadWillStoreFileWithXHR(xhr) {
    xhr.upload.addEventListener("progress", (event) => {
      if (!event.lengthComputable) return;
      const progress = this.row.querySelector("progress");
      if (progress) progress.value = (event.loaded / event.total) * 100;
    });
  }
}
